/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { useDebouncedCallback } from 'use-debounce';
import { FormikHelpers, FormikProps, Field, FieldProps } from 'formik';
import { Grid, InputAdornment, IconButton } from '@material-ui/core';
import Clear from '@material-ui/icons/Clear';

import { selectLockedFields, LockedFields } from 'state/creditApplication';

import useAddressAutocomplete, {
  AddressResult,
} from 'hooks/requests/addressAutocomplete';

import useTranslate from 'hooks/useTranslate';
import Autocomplete from 'components/Autocomplete';
import { FormTextField } from 'components/TextField';

import { stateMask, zipMask } from 'utils/masks';

import withOptional from './withOptional';
import { pickBy } from 'ramda';

const DISABLE_ITEM: AddressResult = {
  formattedAddress: 'Continue typing or click here to enter manually.',
  address1: '',
  city: '',
  state: '',
  zip: '',
};

const useAutocompleteHook = (
  values: FormValues,
  setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
  lockedFields: LockedFields,
): {
  suggestions: AddressResult[];
  onSuggestionSelect: (selection: AddressResult | undefined) => unknown;
  onAddressChange: (e: ChangeEvent<HTMLInputElement>) => Promise<unknown>;
  clearAddress: () => void;
  enabled: boolean;
} => {
  const [suggestions, searchAddress] = useAddressAutocomplete();
  const [enabled, setEnabled] = useState<boolean>(true);

  const isLocked = useCallback(
    (field: string) =>
      field in lockedFields && lockedFields[field as keyof LockedFields],
    [lockedFields],
  );

  const setValue: typeof setFieldValue = useCallback(
    (field, value, shouldValidate) => {
      if (isLocked(field)) {
        return;
      }

      setFieldValue(field, value, shouldValidate);
    },
    [setFieldValue, isLocked],
  );

  const onSuggestionSelect = useCallback(
    (result: AddressResult | undefined) => {
      if (result === undefined) {
        return;
      }

      if (result === DISABLE_ITEM) {
        setEnabled(false);
        return;
      }

      setValue('address1', result.address1);
      setValue('city', result.city);
      setValue('state', result.state);
      setValue('zip', result.zip.substr(0, 5));
    },
    [setValue],
  );

  const [onAddressChange] = useDebouncedCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!enabled) {
        return;
      }

      const partialAddress = pickBy((_value, key) => isLocked(key), values);

      await searchAddress(
        e.target.value + ' ' + (values.zip || ''),
        partialAddress as Partial<AddressResult>,
      );
    },
    50,
  );

  const clearAddress = useCallback(() => {
    setValue('address1', null);
    setValue('city', null);
    setValue('state', null);
    setValue('zip', null);
  }, [setValue]);

  return useMemo(
    () => ({
      suggestions: [...suggestions, DISABLE_ITEM],
      onSuggestionSelect,
      onAddressChange,
      clearAddress,
      enabled,
    }),
    [clearAddress, enabled, onAddressChange, onSuggestionSelect, suggestions],
  );
};

interface FormValues {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

function AddressFields<T extends FormValues>(
  props: FormikProps<T>,
): React.ReactElement {
  const t = useTranslate();
  const lockedFields = useSelector(selectLockedFields);
  const {
    suggestions: suggestionsWithDisable,
    onAddressChange,
    onSuggestionSelect,
    clearAddress,
    enabled,
  } = useAutocompleteHook(props.values, props.setFieldValue, lockedFields);

  const AddressClearButton = (
    <InputAdornment position="end">
      <IconButton
        aria-label="clear address fields"
        onClick={clearAddress}
        edge="end"
      >
        <Clear />
      </IconButton>
    </InputAdornment>
  );

  return (
    <React.Fragment>
      <Grid item xs={12} sm={6}>
        <Field name="firstName">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppFirstName"
              fieldProps={fieldProps}
              label={t('formLabel.firstName')}
              autoComplete="given-name"
              inputProps={{
                maxLength: 15,
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Field name="lastName">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppLastName"
              fieldProps={fieldProps}
              label={t('formLabel.lastName')}
              autoComplete="family-name"
              inputProps={{
                maxLength: 20,
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Field name="address1">
          {(fieldProps: FieldProps) => (
            <Autocomplete
              id="creditAppStreet"
              enabled={enabled}
              inputValue={fieldProps.field.value}
              suggestions={suggestionsWithDisable}
              itemToString={item => (item ? item.formattedAddress : '')}
              onSelect={onSuggestionSelect}
            >
              {({ getInputProps, getLabelProps, inputRef, getRootProps }) => {
                // Callback needs to be passed to the component
                // to compose with its own callbacks.
                // The rest of the props (mostly aria-* attributes)
                // needs to be on the actual input itself. Otherwise,
                // TypeScript gets confused (probably have something to
                // do with the way TextField is typed by MUI)
                const {
                  onBlur,
                  onChange,
                  onKeyDown,
                  ...inputProps
                } = getInputProps({
                  onChange: onAddressChange,
                  autoComplete: 'address-line1',
                });

                return (
                  <FormTextField
                    label={t('formLabel.address1')}
                    ref={inputRef}
                    fieldProps={fieldProps}
                    onBlur={onBlur}
                    fullWidth
                    onChange={onChange as ChangeEventHandler<HTMLInputElement>}
                    onKeyDown={onKeyDown}
                    inputProps={{ ...inputProps, maxLength: 30 }}
                    InputProps={{
                      endAdornment: AddressClearButton,
                      ...getRootProps(
                        { refKey: 'ref' },
                        // This will spread the ref from Downshift into the
                        // Input component, which forward it to the root element,
                        // which should be the div that wrap the input element.
                        // This is correct, but since the root element return to
                        // Downshift is not the div that wraps the input
                        // element, Downshift will raise a ref error.
                        // According to the documentation, we can suppress
                        // the ref error in this case.
                        { suppressRefError: true },
                      ),
                    }}
                    InputLabelProps={{ ...getLabelProps(), required: false }}
                  />
                );
              }}
            </Autocomplete>
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Field name="address2">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppApt"
              required={false}
              label={withOptional(t('formLabel.address2'), false)}
              autoComplete="address-line2"
              fieldProps={fieldProps}
              inputProps={{ maxLength: 30 }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={8} sm={4}>
        <Field name="city">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppCity"
              label={t('formLabel.city')}
              autoComplete="address-level2"
              fieldProps={fieldProps}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={4} sm={2}>
        <Field name="state">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppState"
              label={t('formLabel.state')}
              autoComplete="address-level1"
              fieldProps={fieldProps}
              mask={stateMask}
              pipe={s => s.toUpperCase()}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Field name="zip">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="creditAppZip"
              label={t('formLabel.zip')}
              autoComplete="postal-code"
              fieldProps={fieldProps}
              mask={zipMask}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          )}
        </Field>
      </Grid>
    </React.Fragment>
  );
}

export default AddressFields;
